"use client";
import { RangeField } from "@/components/marketing";
import { cn } from "@/lib/utils";
import type { EvidenceKind, HorizonItem } from "../../data";
import EvidenceTag from "../evidenceTag/evidenceTag.component";
import { useHorizon } from "./hooks";

interface HorizonTimelineProps {
  id: string;
  label: string;
  ariaLabel: string;
  min: number;
  max: number;
  countTemplate: string;
  items: HorizonItem[];
  labels: Record<EvidenceKind, string>;
}

// "The next five years": drag the year forward to reveal what is forecast
// or expected by then.
const HorizonTimeline = ({
  id,
  label,
  ariaLabel,
  min,
  max,
  countTemplate,
  items,
  labels,
}: HorizonTimelineProps) => {
  const { year, setYear, isReached, countText } = useHorizon({
    items,
    min,
    countTemplate,
  });
  const axis = Array.from({ length: max - min + 1 }, (_, index) => min + index);

  return (
    <div className="grid gap-[18px] rounded-[28px] bg-white p-5 shadow-card sm:p-6">
      <div className="grid gap-2">
        <RangeField
          id={id}
          label={label}
          output={String(year)}
          ariaLabel={ariaLabel}
          min={min}
          max={max}
          step={1}
          value={year}
          onChange={setYear}
        />
        <span
          aria-hidden="true"
          className="flex justify-between text-xs text-ink-muted"
        >
          {axis.map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </span>
        <p aria-live="polite" className="text-sm text-ink-muted">
          {countText}
        </p>
      </div>
      <ol className="grid gap-2.5">
        {items.map((item) => {
          const isOn = isReached(item);
          return (
            <li
              key={item.text}
              className={cn(
                "grid gap-1.5 rounded-[18px] border-[1.5px] px-3.5 py-3 transition-colors duration-300 motion-reduce:transition-none sm:grid-cols-[90px_auto_1fr] sm:items-start sm:gap-3",
                isOn
                  ? "border-line bg-white text-ink shadow-card"
                  : "border-dashed border-line-warm bg-paper text-ink-muted",
              )}
            >
              <span className="text-sm font-extrabold">{item.yearLabel}</span>
              <EvidenceTag kind={item.kind} label={labels[item.kind]} />
              <p className="text-[15px] leading-normal">{item.text}</p>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default HorizonTimeline;
